import { z } from "zod";
import { NOTIFICATION_ENTITY_TYPES, NotificationEntityTypeSchema, notificationLink, } from "./events.js";
import { can } from "./permissions.js";
import { optionalQuery } from "./primitives.js";
/**
 * The in-app messenger — a SHARED CONVERSATION between the three people who use
 * this system (§2), optionally about one document.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A MESSAGE IS NOT A NOTIFICATION, AND THE TWO TABLES STAY APART.
 *
 *   NOTIFICATION  targeted AT a person, with that person's own `read_at`. "The
 *                 CA has read it, the owner has not" is a fact it must hold, so
 *                 one event above the threshold is THREE rows.
 *   MESSAGE       written BY a person, addressed to nobody in particular, and
 *                 seen by everyone. One sentence typed is ONE row, for ever.
 *
 * Merging them would force one of the two shapes onto the other: either every
 * message fans out into a row per reader (and "what did dad actually say" has
 * three answers that can drift), or a notification loses its per-person read
 * state, which is the only reason it is a table rather than a derived list.
 * `13_eway_notifications.sql` wrote that split down before either existed; this
 * file is the other half of it.
 *
 * THEY SHARE A SURFACE, NOT A SCHEMA. The client puts both in the same corner of
 * the header and builds both screens out of the same chrome, because the reader
 * should have ONE place to look — see `features/messages/` for why the two
 * badges are still two badges.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * THE OWNER ASKED FOR THIS AND OVERRULED THE OBJECTION. It is built properly and
 * the objection is not re-litigated here; what IS recorded here is the shape the
 * decisions produced, so the next reader inherits the reasoning rather than the
 * argument.
 */
/* -------------------------------------------------------------------------- */
/* 1. The body                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * The longest message the system accepts, in CHARACTERS.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 2,000, AND THE NUMBER IS ALSO THE COLUMN WIDTH (`VarChar(2000)`), so the edge
 * and the database agree. A zod bound looser than its column is a silent trap:
 * the value passes validation and MariaDB then either raises an opaque range
 * error (a 500 where a clean 400 belonged) or, with strict mode relaxed,
 * TRUNCATES — storing a message that says something shorter than what was typed,
 * in a conversation about tax documents. `primitives.ts` makes the same argument
 * for `INDEXED_STRING_MAX`.
 *
 * WHY NOT `TEXT`. This is a message between three people about a bill, not a
 * document. Two thousand characters is around four hundred words — longer than
 * anything anybody will thumb-type on a phone in a workshop — and a bounded
 * column keeps a paste of an entire PDF out of a row that is never deleted.
 *
 * COUNTED IN CHARACTERS, NOT BYTES, and the column is `utf8mb4`, so 2,000
 * Devanagari characters fit exactly as 2,000 Latin ones do (§11.4 — the owner
 * writes in Marathi). A byte cap would silently give him a third of the room.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const MESSAGE_BODY_MAX = 2000;
/**
 * What somebody typed. TRIMMED AT THE ENDS, LENGTH-CAPPED, AND OTHERWISE STORED
 * EXACTLY AS TYPED.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NOTHING HERE STRIPS, ESCAPES OR "SANITISES" THE BODY, AND THAT IS DELIBERATE.
 *
 * The instinct on a text field that will be rendered is to strip angle brackets.
 * It is the wrong instinct HERE, twice over:
 *
 *  1. IT CORRUPTS REAL MESSAGES. "cut the plate <100mm" and "L>2.4m" are things
 *     a fabricator types. A stripper turns the first into "cut the plate 100mm",
 *     which is a different instruction, silently, in a business record.
 *  2. IT PROTECTS NOTHING. The escaping that matters happens at the point of
 *     display, and React escapes `{text}` by construction — the client renders
 *     this as TEXT and never as HTML (no `dangerouslySetInnerHTML`, no markdown
 *     renderer, anywhere in this feature). Sanitising at the edge as well would
 *     be a second, weaker defence that damages the data to duplicate the first.
 *
 * So the rule is: the API stores the characters it was given, and every surface
 * renders them as text. If a future surface ever wants HTML, that is a change to
 * argue for at the RENDERER, never a change to this schema.
 *
 * `.trim()` is not sanitising: a message that is only spaces and newlines is not
 * a message, and trailing whitespace off a phone keyboard is noise nobody typed
 * on purpose. Internal newlines survive — a two-line message stays two lines.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const MessageBodySchema = z
    .string({ required_error: "Type a message first." })
    .trim()
    .min(1, "Type a message first.")
    .max(MESSAGE_BODY_MAX, `A message must be ${MESSAGE_BODY_MAX} characters or fewer.`);
/* -------------------------------------------------------------------------- */
/* 2. The attachment — "about invoice 26-27/67"                                */
/* -------------------------------------------------------------------------- */
/**
 * The document kinds a message can be about.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DELIBERATELY THE SAME CONSTANT THE NOTIFICATION HUB USES, ALIASED RATHER THAN
 * COPIED. A second list would let a fifth document kind become linkable from an
 * alert and not from a message — two answers to "what can this app deep-link
 * to?", which is exactly the drift `notificationLink` was centralised to prevent.
 *
 * The alias exists at all because the name matters at the call site: a route
 * handler for the messenger reading `NotificationEntityType` would be reading a
 * type that names the wrong feature, and the next person would copy the list
 * rather than the import.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const MESSAGE_ATTACHMENT_TYPES = NOTIFICATION_ENTITY_TYPES;
export const MessageAttachmentTypeSchema = NotificationEntityTypeSchema;
/**
 * WHICH CAPABILITY LETS A READER OPEN THE ATTACHED DOCUMENT (§2.1).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE MESSAGE IS ALWAYS SHOWN. THE LINK IS NOT — AND THAT ASYMMETRY IS THE POINT.
 *
 * A message is a conversation between three people; withholding "have you looked
 * at 26-27/67?" from one of them would be censoring the conversation, not
 * enforcing a permission. But the LINK is a promise that tapping it opens the
 * bill, and the SPA's route guards send anybody without the `*.view` capability
 * straight back to the home screen. A link that bounces the reader Home with no
 * explanation reads as the app ignoring the tap — the exact failure `paths.ts`
 * catalogues for the counters screen.
 *
 * So the server resolves the link PER READER: hold the capability and the row
 * carries a destination; do not, and it carries the label as plain text. Nobody
 * is misled and nobody is silenced.
 *
 * A TOTAL `Record` over the union, so a fifth document kind fails to compile
 * here rather than shipping a link nobody checked.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const MESSAGE_ATTACHMENT_CAPABILITY = Object.freeze({
    invoice: "invoice.view",
    challan: "challan.view",
    quotation: "quotation.view",
    creditNote: "creditnote.view",
});
/**
 * May this reader open a document of this kind?
 *
 * Deny by default, through the one resolver (§2.1): an unknown role, or a kind
 * outside the union at runtime, answers `false` and the row renders as text.
 * `MESSAGE_ATTACHMENT_CAPABILITY` is a plain frozen object, so an untrusted
 * `type` is CHECKED against the union before it is used as a key — otherwise
 * `"toString"` would index `Object.prototype` and hand `can()` a function.
 */
export function canOpenAttachment(role, type) {
    if (!isMessageAttachmentType(type))
        return false;
    return can(role, MESSAGE_ATTACHMENT_CAPABILITY[type]);
}
export function isMessageAttachmentType(value) {
    return typeof value === "string" && MESSAGE_ATTACHMENT_TYPES.includes(value);
}
/**
 * Where the attachment lands in the SPA.
 *
 * THE SAME BUILDER THE HUB USES, for the reason the type list above is shared:
 * `/invoices/67` must be spelled in exactly one place, or the day the route
 * changes, one of the two surfaces goes on sending people to the old one.
 */
export function messageAttachmentLink(type, id) {
    return notificationLink(type, id);
}
/* -------------------------------------------------------------------------- */
/* 3. The request contracts                                                    */
/* -------------------------------------------------------------------------- */
/**
 * The document a message is about, as the CLIENT names it: a kind and an id.
 *
 * `.strict()`, so a client that also sends `label` is a 400 rather than a
 * message whose caption it chose. The label is SNAPSHOTTED SERVER-SIDE from the
 * document itself (see `services/messages.ts`) — "about invoice 26-27/67" has to
 * be the number the bill actually carries, not a string somebody typed.
 */
export const MessageAttachmentSchema = z
    .object({
    type: MessageAttachmentTypeSchema,
    id: z
        .number({ invalid_type_error: "Attachment id must be a number" })
        .int("Attachment id must be a whole number")
        .positive("Attachment id must be positive"),
})
    .strict();
/**
 * `POST /messages`.
 *
 * `.strict()` like every other write contract here. Note what a client CANNOT
 * send: the author (taken from the session), the timestamp (the database's), the
 * attachment label (read off the document), or an id. Everything a message
 * asserts about who said what and when is the server's, which is what makes the
 * row worth anything in a dispute months later.
 */
export const MessageSendSchema = z
    .object({
    body: MessageBodySchema,
    /** Absent for the ordinary case — a plain message, about nothing. */
    attachment: MessageAttachmentSchema.optional(),
})
    .strict();
/**
 * `POST /messages/read` — move MY watermark forward.
 *
 * The id is the newest message the reader has actually had on screen, so the
 * client sends what it rendered rather than the server assuming "everything".
 * A watermark never moves BACKWARDS (see the service), so an out-of-order or
 * replayed request cannot resurrect an unread badge.
 */
export const MessageReadSchema = z
    .object({
    upToId: z
        .number({ invalid_type_error: "upToId must be a number" })
        .int("upToId must be a whole number")
        .positive("upToId must be positive"),
})
    .strict();
/**
 * `GET /messages?limit=&before=`.
 *
 * `.strict()`, so an unknown key is a 400 rather than a filter that appears to
 * work. `optionalQuery` on both, because the SPA sends every key whether or not
 * it is set — `?before=` must mean "the newest page", not `Number("") === 0`.
 *
 * THERE IS NO `offset`, AND THAT IS NOT AN OMISSION. Offset paging over a table
 * that is being appended to shifts under the reader: a message sent while he is
 * scrolling pushes everything down one, and page 2 repeats the row page 1 ended
 * on. An id cursor cannot do that — `before` names a fixed point in a sequence
 * that only ever grows.
 */
export const MessageListQuerySchema = z
    .object({
    /*
     * THE `.default()` GOES INSIDE `optionalQuery`, NOT AFTER IT, AND THE ORDER
     * IS LOAD-BEARING. `optionalQuery` is a `z.preprocess` that rewrites `""` to
     * `undefined`; a `.default()` applied OUTSIDE it only fires when the raw
     * INPUT was undefined, so `?limit=` would reach `z.coerce.number()` as
     * `undefined`, coerce to `NaN` and answer 400 on a request the SPA makes
     * every time the field is left blank. Inside, the default sees the rewritten
     * `undefined` and supplies 50.
     */
    limit: optionalQuery(z.coerce.number().int().min(1).max(100).default(50)),
    /** Return the messages immediately BEFORE this id — "load older". */
    before: optionalQuery(z.coerce.number().int().positive().optional()),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* 5. Direct messages — the same messenger, addressed to ONE person            */
/* -------------------------------------------------------------------------- */
/**
 * `/messages/direct/...` (2026-08-18) — a conversation between two people.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SHARED ROOM IS UNCHANGED BY THIS. Everything above still describes rows
 * addressed to nobody in particular, and those rows are still the ones
 * `GET /messages` returns. A direct message is the SAME TABLE with a recipient
 * on it, and the room's queries now say `recipient IS NULL` so the two never mix
 * — see `services/messages.ts`.
 *
 * A CONVERSATION IS IDENTIFIED BY THE OTHER PERSON, NOT BY AN ID. Three people
 * make three possible pairs; a `conversations` table with a `conversation_id`
 * would add two tables, a find-or-create with a race on it, and a unique key on
 * the canonical pair to close that race — all to name something the pair already
 * names exactly. The cost is stated rather than hidden: THIS SHAPE CANNOT DO
 * GROUP DMs. If a fourth participant is ever wanted in one conversation, that is
 * a conversation table and a migration, and this design does not pretend
 * otherwise. It does not block one either — `recipient_user_id` stays NULL for
 * the room, and a `conversation_id` could join it later.
 *
 * THE READER IS ALWAYS ONE HALF OF THE PAIR, TAKEN FROM THE SESSION. There is no
 * route that names both participants, so there is no route on which somebody
 * could ask for a conversation they are not in. That is the privacy guarantee,
 * and it is structural rather than a check somebody has to remember to write.
 *
 * WHAT A DIRECT MESSAGE IS NOT: it is not deletable, not editable, and not
 * invisible to §13. The send writes an audit row like every other mutation. What
 * that row deliberately omits is the BODY — see `services/direct-messages.ts`.
 * ═════════════════════════════════════════════════════════════════════════════
 */
/**
 * `:userId` — the OTHER person in the conversation.
 *
 * Not `IdParamSchema`, whose key is `id`: `id` in this API means "the id of the
 * thing at this path", and the thing at this path is a conversation, not a user.
 * Naming the parameter for what it holds is what stops the next reader passing
 * it to `prisma.message.findUnique`.
 *
 * Not `.strict()`, for the reason `IdParamSchema` is not: this router is mounted
 * under `/messages`, and a parent's params legitimately ride along.
 */
export const DirectPeerParamSchema = z.object({
    userId: z.coerce
        .number({ invalid_type_error: "A user id must be a number" })
        .int("A user id must be a whole number")
        .positive("A user id must be positive"),
});
/**
 * `POST /messages/direct/:userId`.
 *
 * DELIBERATELY THE SAME SCHEMA AS THE ROOM'S, ALIASED RATHER THAN COPIED — the
 * body, the length cap and the attachment rules are identical, and a second
 * declaration is two places for `MESSAGE_BODY_MAX` to drift from the column.
 *
 * THE RECIPIENT IS IN THE URL AND NOT IN THE BODY, WHICH IS WHY THE ALIAS WORKS.
 * `.strict()` therefore refuses `{ body, recipientUserId }` with a 400: a client
 * that could name the recipient twice is a client that can name two different
 * ones, and the one the server would believe is not the one the URL shows.
 */
export const DirectMessageSendSchema = MessageSendSchema;
/** `POST /messages/direct/:userId/read` — the same watermark contract. */
export const DirectMessageReadSchema = MessageReadSchema;
/** `GET /messages/direct/:userId?limit=&before=` — the same cursor paging. */
export const DirectMessageListQuerySchema = MessageListQuerySchema;
//# sourceMappingURL=messages.js.map